import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useAuth } from "../hooks/useAuth";
import { useCreateProfile } from "../hooks/useCreateProfile";
import { Button } from "@patient-watch/ui";
import { PageLayout } from "../components/PageLayout";
import { AlertCircle, CheckCircle, UserPlus } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useCopy } from "../i18n/locale";

export const Route = createFileRoute("/profile")({
    component: ProfileComponent,
});

function ProfileComponent() {
    const { isAuthenticated, user, profile, phone, hasProfile, isLoading } = useAuth();
    const router = useRouter();
    const queryClient = useQueryClient();
    const createProfileMutation = useCreateProfile();
    const [showContent, setShowContent] = useState(false);
    const copy = useCopy();

    // Add 1000ms timeout before showing content
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowContent(true);
        }, 1000);

        return () => clearTimeout(timer);
    }, []);

    // Show loading state while auth is being determined or during timeout
    if (isLoading || !showContent) {
        return (
            <PageLayout title={copy.common.loading} description={copy.common.checkingAuth} icon={UserPlus} theme="blue">
                <div className="text-center animate-pulse">
                    <p className="text-slate-600 text-lg">{copy.profile.checking}</p>
                </div>
            </PageLayout>
        );
    }

    // Redirect if not authenticated (only after loading is complete)
    if (!isAuthenticated || !user) {
        router.navigate({ to: "/login" });
        return null;
    }

    const handleCreateProfile = () => {
        createProfileMutation.mutate(phone);
    };

    const handleProfileSuccess = () => {
        router.navigate({ to: "/instructions" });
    };

    // Handle successful profile creation
    if (createProfileMutation.isSuccess) {
        queryClient.invalidateQueries({ queryKey: ["profile", user?.id] });
    }

    // Profile exists - show success state
    if (hasProfile && profile) {
        return (
            <PageLayout
                title={copy.profile.registeredTitle}
                description={copy.profile.registeredDescription}
                icon={CheckCircle}
                theme="green"
            >
                <Button onClick={handleProfileSuccess} className="w-full" size="lg">
                    {copy.profile.continue}
                </Button>
            </PageLayout>
        );
    }

    // No profile exists - show creation form (this is expected for new users)
    return (
        <PageLayout title={copy.profile.title} description={copy.profile.description} icon={UserPlus} theme="blue">
            {createProfileMutation.error && (
                <div className="flex items-center gap-2 mb-4 text-red-600 text-sm">
                    <AlertCircle size={20} />
                    {createProfileMutation.error.message}
                </div>
            )}

            <Button
                onClick={handleCreateProfile}
                className="w-full"
                size="lg"
                disabled={createProfileMutation.isPending}
            >
                {createProfileMutation.isPending ? copy.profile.joining : copy.profile.join}
            </Button>
        </PageLayout>
    );
}
